import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  HelpOutline as InfoIcon,
  AttachMoney as PayIcon,
  Send as SubmitIcon,
} from '@mui/icons-material';

import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { requestService } from '../../services/requestService';
import { UserRole } from '../../types/auth.types';
import { useAppSelector } from '../../store';

interface RequestWorkflowProps {
  request: PurchaseRequest;
  onStatusChange: () => void;
}

const RequestWorkflow: React.FC<RequestWorkflowProps> = ({ request, onStatusChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'info' | 'pay' | 'submit' | null>(null);
  const [comment, setComment] = useState('');
  const [creditCardLastFour, setCreditCardLastFour] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const user = useAppSelector(state => state.auth.user);

  // Pre-populate payment fields when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      // Pre-populate payment fields with existing values from request
      setPaymentType(request.paymentType || '');
      setPaymentReference(request.paymentReference || '');
    } else {
      // Clear form when dialog closes
      setComment('');
      setCreditCardLastFour('');
      if (!request.paymentType) {
        setPaymentType('');
        setPaymentReference('');
      }
    }
  }, [dialogOpen, request.paymentType, request.paymentReference]);

  const getPaymentReferenceHint = () => {
    switch (paymentType) {
      case 'personal_credit_card':
      case 'company_credit_card':
        return 'Enter the last 4 digits of the credit card';
      case 'transfer':
        return 'Enter the reference number of the transfer';
      case 'cash':
        return 'Enter the reference number of the cash payment';
      default:
        return 'Enter payment reference';
    }
  };

  const getPaymentReferenceLabel = () => {
    switch (paymentType) {
      case 'personal_credit_card':
      case 'company_credit_card':
        return 'Last 4 digits of credit card';
      case 'transfer':
        return 'Transfer reference number';
      case 'cash':
        return 'Cash reference number';
      default:
        return 'Payment reference';
    }
  };

  const handlePaymentReferenceChange = (value: string) => {
    if (paymentType === 'personal_credit_card' || paymentType === 'company_credit_card') {
      // For credit cards, only allow 4 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
      setPaymentReference(digitsOnly);
    } else {
      // For transfer and cash, allow alphanumeric
      setPaymentReference(value);
    }
  };

  const handleAction = async () => {
    try {
      setError(null);
      
      switch (actionType) {
        case 'submit':
          await requestService.submitRequest(request.id);
          break;
        
        case 'approve':
          if (user?.role === UserRole.DEPARTMENT_MANAGER) {
            // Department manager approval - route to General Manager for final approval
            await requestService.updateRequestStatus(request.id, RequestStatus.PENDING_GENERAL_MANAGER, comment, paymentType, paymentReference);
          } else if (user?.role === UserRole.SENIOR_MANAGER) {
            // Senior manager approval - route to CFO or General Manager based on backend logic
            await requestService.updateRequestStatus(request.id, RequestStatus.PENDING_CFO, comment, paymentType, paymentReference);
          } else if (user?.role === UserRole.GENERAL_MANAGER) {
            // General manager approval - send to finance for payment processing
            await requestService.updateRequestStatus(request.id, RequestStatus.PENDING_FINANCE, comment, paymentType, paymentReference);
          } else if (user?.role === UserRole.CFO) {
            await requestService.updateRequestStatus(request.id, RequestStatus.APPROVED, comment, paymentType, paymentReference);
          }
          break;
        
        case 'reject':
          await requestService.updateRequestStatus(request.id, RequestStatus.REJECTED, comment, paymentType, paymentReference);
          break;
        
        case 'info':
          await requestService.requestMoreInfo(request.id, comment, paymentType, paymentReference);
          break;
        
        case 'pay':
          await requestService.markAsPaid(request.id, paymentType, paymentReference);
          break;
      }
      
      setDialogOpen(false);
      setComment('');
      setCreditCardLastFour('');
      setPaymentType('');
      setPaymentReference('');
      onStatusChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request status');
    }
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.DRAFT:
        return 'default';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      case RequestStatus.APPROVED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const canApprove = () => {
    if (!user) return false;
    
    if (user.role === UserRole.DEPARTMENT_MANAGER) {
      return request.status === RequestStatus.PENDING_MANAGER && 
             request.departmentId === user.departmentId;
    }
    
    if (user.role === UserRole.SENIOR_MANAGER) {
      return request.status === RequestStatus.PENDING_SENIOR_MANAGER;
    }
    
    if (user.role === UserRole.GENERAL_MANAGER) {
      return request.status === RequestStatus.PENDING_GENERAL_MANAGER;
    }
    
    // Finance Admin cannot approve - they can only mark as paid
    // if (user.role === UserRole.FINANCE_ADMIN) {
    //   return request.status === RequestStatus.PENDING_FINANCE;
    // }
    
    if (user.role === UserRole.CFO) {
      return request.status === RequestStatus.PENDING_CFO;
    }
    
    return false;
  };

  const canReject = () => {
    if (!user) return false;
    return (
      (user.role === UserRole.DEPARTMENT_MANAGER && request.status === RequestStatus.PENDING_MANAGER) ||
      (user.role === UserRole.SENIOR_MANAGER && request.status === RequestStatus.PENDING_SENIOR_MANAGER) ||
      (user.role === UserRole.GENERAL_MANAGER && request.status === RequestStatus.PENDING_GENERAL_MANAGER) ||
      (user.role === UserRole.CFO && request.status === RequestStatus.PENDING_CFO)
    );
  };

  const canRequestInfo = () => {
    if (!user) return false;
    return (
      (user.role === UserRole.DEPARTMENT_MANAGER && request.status === RequestStatus.PENDING_MANAGER) ||
      (user.role === UserRole.SENIOR_MANAGER && request.status === RequestStatus.PENDING_SENIOR_MANAGER) ||
      (user.role === UserRole.GENERAL_MANAGER && request.status === RequestStatus.PENDING_GENERAL_MANAGER) ||
      (user.role === UserRole.CFO && request.status === RequestStatus.PENDING_CFO)
    );
  };

  const canMarkAsPaid = () => {
    if (!user) return false;
    return user.role === UserRole.FINANCE_ADMIN && 
           (request.status === RequestStatus.PENDING_FINANCE || request.status === RequestStatus.APPROVED);
  };

  const canSubmit = () => {
    if (!user) return false;
    // Any user can submit their own requests (employees, department managers, etc.)
    return (request.status === RequestStatus.DRAFT || request.status === RequestStatus.MORE_INFO_NEEDED) && 
           request.requesterId === user.id;
  };

  const getSubmitButtonText = () => {
    return request.status === RequestStatus.MORE_INFO_NEEDED ? 'Resubmit Request' : 'Submit Request';
  };
  
  const getSubmitDialogTitle = () => {
    return request.status === RequestStatus.MORE_INFO_NEEDED ? 'Resubmit Request' : 'Submit Request for Approval';
  };
  
  const getSubmitDialogContent = () => {
    if (request.status === RequestStatus.MORE_INFO_NEEDED) {
      return 'Are you sure you want to resubmit this request for manager review? Make sure you have provided all the requested information in the comments.';
    }
    return 'Are you sure you want to submit this request for manager approval? Once submitted, you won\'t be able to edit the request details.';
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Request Status
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="body1">Current Status:</Typography>
            <Chip
              label={request.status.replace(/_/g, ' ')}
              color={getStatusColor(request.status)}
            />
          </Stack>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {canSubmit() && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SubmitIcon />}
                onClick={() => {
                  setActionType('submit');
                  setDialogOpen(true);
                }}
              >
                {getSubmitButtonText()}
              </Button>
            )}

            {canApprove() && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => {
                  setActionType('approve');
                  setDialogOpen(true);
                }}
              >
                Approve
              </Button>
            )}

            {canReject() && (
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => {
                  setActionType('reject');
                  setDialogOpen(true);
                }}
              >
                Reject
              </Button>
            )}

            {canRequestInfo() && (
              <Button
                variant="contained"
                color="info"
                startIcon={<InfoIcon />}
                onClick={() => {
                  setActionType('info');
                  setDialogOpen(true);
                }}
              >
                Request Info
              </Button>
            )}

            {canMarkAsPaid() && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PayIcon />}
                onClick={() => {
                  setActionType('pay');
                  setDialogOpen(true);
                }}
              >
                Mark as Paid
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'submit' && getSubmitDialogTitle()}
          {actionType === 'approve' && 'Approve Request'}
          {actionType === 'reject' && 'Reject Request'}
          {actionType === 'info' && 'Request Additional Information'}
          {actionType === 'pay' && 'Mark Request as Paid'}
        </DialogTitle>
        
        <DialogContent>
          {actionType === 'submit' && (
            <Typography>
              {getSubmitDialogContent()}
            </Typography>
          )}
          
          {actionType === 'pay' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="payment-type-label">Payment Type</InputLabel>
                <Select
                  labelId="payment-type-label"
                  value={paymentType}
                  label="Payment Type"
                  onChange={(e) => setPaymentType(e.target.value as string)}
                  required
                >
                  <MenuItem value="personal_credit_card">Personal Credit Card</MenuItem>
                  <MenuItem value="company_credit_card">Company Credit Card</MenuItem>
                  <MenuItem value="transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                </Select>
              </FormControl>
              
              {paymentType && (
                <TextField
                  margin="dense"
                  id="payment-reference"
                  label={getPaymentReferenceLabel()}
                  type="text"
                  fullWidth
                  value={paymentReference}
                  onChange={(e) => handlePaymentReferenceChange(e.target.value)}
                  required
                  inputProps={{
                    maxLength: paymentType === 'personal_credit_card' || paymentType === 'company_credit_card' ? 4 : 50
                  }}
                  helperText={getPaymentReferenceHint()}
                />
              )}
            </>
          )}
          
          {actionType !== 'pay' && actionType !== 'submit' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                id="comment"
                label={actionType === 'info' ? 'What additional information is needed?' : 'Comment (Optional)'}
                type="text"
                fullWidth
                multiline
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required={actionType === 'info' || actionType === 'reject'}
              />
              
              {/* Payment Information - Available for all approval actions */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Payment Information (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You can update payment details now or leave them to be filled later.
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="payment-type-label">Payment Type</InputLabel>
                <Select
                  labelId="payment-type-label"
                  value={paymentType}
                  label="Payment Type"
                  onChange={(e) => setPaymentType(e.target.value as string)}
                >
                  <MenuItem value="">
                    <em>Select payment type (optional)</em>
                  </MenuItem>
                  <MenuItem value="personal_credit_card">Personal Credit Card</MenuItem>
                  <MenuItem value="company_credit_card">Company Credit Card</MenuItem>
                  <MenuItem value="transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                </Select>
              </FormControl>
              
              {paymentType && (
                <TextField
                  margin="dense"
                  id="payment-reference"
                  label={getPaymentReferenceLabel()}
                  type="text"
                  fullWidth
                  value={paymentReference}
                  onChange={(e) => handlePaymentReferenceChange(e.target.value)}
                  inputProps={{
                    maxLength: paymentType === 'personal_credit_card' || paymentType === 'company_credit_card' ? 4 : 50
                  }}
                  helperText={getPaymentReferenceHint()}
                />
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAction}
            variant="contained"
            color={
              actionType === 'submit' ? 'primary' :
              actionType === 'approve' ? 'success' :
              actionType === 'reject' ? 'error' :
              actionType === 'info' ? 'info' :
              'secondary'
            }
            disabled={
              (actionType === 'info' && !comment) ||
              (actionType === 'reject' && !comment) ||
              (actionType === 'pay' && (!paymentType || !paymentReference))
            }
          >
            {actionType === 'submit' && 'Submit'}
            {actionType === 'approve' && 'Confirm'}
            {actionType === 'reject' && 'Confirm'}
            {actionType === 'info' && 'Confirm'}
            {actionType === 'pay' && 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestWorkflow; 